// @flow
// eslint-disable-next-line max-classes-per-file
import { List, Record } from 'immutable';
import type { RecordOf, RecordFactory } from 'immutable';

import { mkAccount } from 'data/accounts/types';
import type { Account } from 'data/accounts/types';
import type { InstitutionLogin, MfaChallenge } from 'data/institutionLogins/types';
import ExtendableError from 'es6-error';

export const DIALOG_TYPE = 'DIALOG_ACCOUNT_DISCOVERY';
export type AccountDiscoveryMode =
  'ADD-FI' | 'UPDATE-FI' | 'ACCOUNT-REDISCOVERY' | 'UPDATE-CREDENTIALS' | 'RE-AUTHENTICATE' | 'PROVIDE-CREDENTIALS' | 'PROVIDE-MFA';

type AccountDiscoveryDialogPropsProps = {
  mode: AccountDiscoveryMode,
  initiator: ?string,

  institutionLogin: ?InstitutionLogin,
  mfaChallenge: ?MfaChallenge,
  hideBackdrop: ?boolean,

  afterCloseAction: ?any,
}
export type AccountDiscoveryDialogProps = RecordOf<AccountDiscoveryDialogPropsProps>;
export const mkAccountDiscoveryDialogProps: RecordFactory<AccountDiscoveryDialogPropsProps> =
  Record({
    mode: 'ADD-FI',
    initiator: null,

    institutionLogin: null,
    mfaChallenge: null,
    hideBackdrop: false,

    afterCloseAction: null,
  });

// =============================================================================
// Account Discovery Data
// =============================================================================

type DiscoveredAccountProps = {
  id: string,
  channel: string,

  type: string,
  subType: ?string,

  name: string,
  number: string,
  maskedNumber: string,

  balance: number,
  balanceAt: string,

  currency: string,
};
export type DiscoveredAccount = RecordOf<DiscoveredAccountProps>;
export const mkDiscoveredAccount: RecordFactory<DiscoveredAccountProps> =
  Record({
    id: null,
    channel: null,

    type: null,
    subType: null,

    name: null,
    number: null,
    maskedNumber: null,

    balance: null,
    balanceAt: null,

    currency: null,
  });

type ActiveDiscoveredAccountProps = {
  account: Account,
  discoveredAccount: DiscoveredAccount
}
export type ActiveDiscoveredAccount = RecordOf<ActiveDiscoveredAccountProps>;
export const mkActiveDiscoveredAccount: RecordFactory<ActiveDiscoveredAccountProps> =
  Record({
    account: mkAccount('UNKNOWN'),
    discoveredAccount: mkDiscoveredAccount(),
  });

type AccountDiscoveryDataProps = {
  institutionLoginId: string,
  credentialsBlob: ?string,

  mode: string,
  status: string,

  activeAccounts: ?List<ActiveDiscoveredAccount>,
  deadAccounts: ?List<Account>,
  newAccounts: ?List<DiscoveredAccount>,
  unconnectedAccounts: ?List<Account>,
};
export type AccountDiscoveryData = RecordOf<AccountDiscoveryDataProps>;
export const mkAccountDiscoveryData: RecordFactory<AccountDiscoveryDataProps> =
  Record({
    institutionLoginId: null,
    credentialsBlob: null,

    status: null,

    activeAccounts: null,
    deadAccounts: null,
    newAccounts: null,
    unconnectedAccounts: null,
  });

// =============================================================================
// InstitutionLoginUpserter
// =============================================================================

type InstitutionLoginUpserterStoreProps = {
  institutionId: ?string,
  institutionLogin: ?InstitutionLogin,

  // contains MFA challenge data if server has requested an MFA challenge
  mfaChallenge: ?MfaChallenge,

  // contains partnerAuthUris after oauth flow (if authType is PARTNER_AUTH
  partnerAuthUris: ?List<string>,

  // used for Plaid when re-authenticating an existing login
  publicToken: ?string,

  // contains discovered accounts data (if discovering accounts and discovered data is available
  accountDiscoveryData: ?AccountDiscoveryData,

  isAuthenticationDone: boolean,
  isAccountUpsertingDone: boolean,
  isAccountRefreshingDone: boolean,

  isPolling: false,
  isSubmitting: boolean,

  error: ?Object,
};
export type InstitutionLoginUpserterStore = RecordOf<InstitutionLoginUpserterStoreProps>;
export const mkInstitutionLoginUpserterStore: RecordFactory<InstitutionLoginUpserterStoreProps> =
  Record({
    institutionId: null,
    institutionLogin: null,

    mfaChallenge: null,
    partnerAuthUris: null,
    publicToken: null,

    accountDiscoveryData: null,

    isAuthenticationDone: false,
    isAccountUpsertingDone: false,
    isAccountRefreshingDone: false,

    isPolling: false,
    isSubmitting: false,

    error: null,
  });

// ================================================================================================
// Types AccountDiscovery Status
// ================================================================================================

type AccountDiscoveryAggregatorStatusProps = {
  isProcessing: boolean,
  aggStatus: string,
  cpAggStatusCode: ?string,
  cpAggStatusDetail: ?string,
};
export type AccountDiscoveryAggregatorStatus = RecordOf<AccountDiscoveryAggregatorStatusProps>;
export const mkAccountDiscoveryAggregatorStatus: RecordFactory<AccountDiscoveryAggregatorStatusProps> =
  Record({
    isProcessing: false,

    aggStatus: 'unknown',

    cpAggStatusCode: null,
    cpAggStatusDetail: null,
  });


type AccountDiscoveryStatusProps = {
  institutionLoginId: string,
  isProcessing: boolean,
  status: string,
  aggregators: List<AccountDiscoveryAggregatorStatus>,

}
export type AccountDiscoveryStatus = RecordOf<AccountDiscoveryStatusProps>;
export const mkAccountDiscoveryStatus: RecordFactory<AccountDiscoveryStatusProps> =
  Record({
    institutionLoginId: null,
    isProcessing: false,
    status: '',
    aggregators: List(),
  });

export class AccountDiscoveryCredentialsFormSubmitError extends ExtendableError {

}

export class AccountDiscoveryMfaFormSubmitError extends ExtendableError {

}

// ================================================================================================
// Account Upserter Types
// ================================================================================================

export type ACCOUNT_ACTION_TYPES = 'ADD_ACCOUNT' | 'IGNORE' | 'LINK_TO';

export type AccountActionProps = {
  id: string,
  type: ACCOUNT_ACTION_TYPES,
  menuItemLabel: string,
  selectedItemLabel: string,
  accountToLinkTo: ?Account,
}
export type AccountAction = RecordOf<AccountActionProps>;
export const mkAccountAction: RecordFactory<AccountActionProps> =
  Record({
    id: '0',
    type: 'LINK_TO',
    menuItemLabel: 'Link To',
    selectedItemLabel: 'Link',
    accountToLinkTo: null,
  });

export const ACCOUNT_ACTION_ADD = mkAccountAction({
  id: 'ADD',
  type: 'ADD_ACCOUNT',
  menuItemLabel: 'Add',
  selectedItemLabel: 'Add It',
});
export const ACCOUNT_ACTION_IGNORE = mkAccountAction({
  id: 'IGNORE',
  type: 'IGNORE',
  menuItemLabel: 'Ignore',
  selectedItemLabel: 'Ignore',
});

export type AccountData = {
  action: AccountAction,

  name: string,
  type: string,
  discoveredAccount: DiscoveredAccount,

  linkableAccountsSection1: ?List<Account>,
  linkableAccountsSection2: ?List<Account>,
  linkableAccountsSection3: ?List<Account>,

  formFieldName: string,
};

